import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { providersApi, ProviderPublic } from '../services/providers';

// Search Icon component
const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9 17C13.4183 17 17 13.4183 17 9C17 4.58172 13.4183 1 9 1C4.58172 1 1 4.58172 1 9C1 13.4183 4.58172 17 9 17Z"
      stroke="#1f1f1f"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M19 19L14.65 14.65"
      stroke="#1f1f1f"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface SearchBarProps {
  placeholder?: string;
  className?: string;
  onSearch?: (query: string) => void;
  isAuthenticated?: boolean;
}

const SearchBar: React.FC<SearchBarProps> = ({
  placeholder = 'Search for providers',
  className = '',
  onSearch,
  isAuthenticated = true,
}) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState<ProviderPublic[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Fetch suggestions with debounce
  useEffect(() => {
    if (query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      setLoading(false);
      return;
    }

    // Ignore responses for queries that are no longer current
    let cancelled = false;

    const timeoutId = setTimeout(async () => {
      try {
        setLoading(true);
        // Fetch suggestions and providers in parallel
        const [suggestionResults, providerResults] = await Promise.all([
          providersApi.getSearchSuggestions(query),
          providersApi.searchProviders(query, 0, 20),
        ]);
        if (cancelled) return;
        setSuggestions(suggestionResults);
        setProviders(providerResults);
        setShowSuggestions(true);
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
        setProviders([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300); // 300ms debounce

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [query, isAuthenticated]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0) {
        handleSuggestionClick(suggestions[selectedIndex]);
      } else {
        handleSearch();
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Signed-out users can't use /search: open the first matching public profile, or sign in
  const goToPublicResult = (results: ProviderPublic[]) => {
    navigate(results.length > 0 ? `/provider/public/${results[0].id}` : '/signin');
  };

  const handleSuggestionClick = async (suggestion: string) => {
    setQuery(suggestion);
    setShowSuggestions(false);
    setSelectedIndex(-1);

    // Check if the suggestion is a provider's business name
    const matchingProvider = providers.find(
      (p) => p.business_name.toLowerCase() === suggestion.toLowerCase()
    );

    if (matchingProvider) {
      // Navigate to public or authenticated provider profile
      if (isAuthenticated) {
        navigate(`/provider/${matchingProvider.id}`);
      } else {
        navigate(`/provider/public/${matchingProvider.id}`);
      }
    } else if (onSearch) {
      onSearch(suggestion);
    } else if (isAuthenticated) {
      // Navigate to search results page for specialties or cities
      navigate(`/search?q=${encodeURIComponent(suggestion)}`);
    } else {
      try {
        goToPublicResult(await providersApi.searchProviders(suggestion, 0, 10));
      } catch (error) {
        console.error('Error searching providers:', error);
        navigate('/signin');
      }
    }
  };

  const handleSearch = async () => {
    if (query.trim()) {
      setShowSuggestions(false);

      // Check if query exactly matches a provider name
      try {
        const searchResults = await providersApi.searchProviders(query, 0, 10);
        const exactMatch = searchResults.find(
          (p) => p.business_name.toLowerCase() === query.toLowerCase()
        );

        if (exactMatch) {
          if (isAuthenticated) {
            navigate(`/provider/${exactMatch.id}`);
          } else {
            navigate(`/provider/public/${exactMatch.id}`);
          }
        } else if (onSearch) {
          onSearch(query);
        } else if (isAuthenticated) {
          navigate(`/search?q=${encodeURIComponent(query)}`);
        } else {
          goToPublicResult(searchResults);
        }
      } catch (error) {
        console.error('Error searching providers:', error);
        if (onSearch) {
          onSearch(query);
        } else if (isAuthenticated) {
          navigate(`/search?q=${encodeURIComponent(query)}`);
        } else {
          navigate('/signin');
        }
      }
    }
  };

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => query.length >= 2 && suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          className="w-full px-4 py-2 pr-10 text-sm font-['Open_Sans'] text-[#1f1f1f] placeholder-[#767472] bg-white border border-[#c6a664] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#c6a664] focus:border-transparent"
        />
        <button
          onClick={handleSearch}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
        >
          <SearchIcon />
        </button>
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && (suggestions.length > 0 || loading) && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-[#eaeaea] rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {loading ? (
            <div className="px-4 py-3 text-sm text-[#767472]">
              <div className="animate-pulse">Searching...</div>
            </div>
          ) : (
            suggestions.map((suggestion, index) => (
              <div
                key={index}
                className={`px-4 py-3 text-sm font-['Open_Sans'] cursor-pointer transition-colors ${
                  index === selectedIndex
                    ? 'bg-[#f8f6f2] text-[#1f1f1f]'
                    : 'text-[#1f1f1f] hover:bg-[#f8f6f2]'
                }`}
                onClick={() => handleSuggestionClick(suggestion)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                {suggestion}
              </div>
            ))
          )}
        </div>
      )}

      {/* No authentication message - removed since we now allow public search */}
    </div>
  );
};

export default SearchBar;
